import React, { useEffect, useRef } from "react";
import {
  FiBriefcase,
  FiFileText,
  FiCreditCard,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./PatientsPage.css";

gsap.registerPlugin(ScrollTrigger);

const PatientsPage = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Scroll stagger animations for cards
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".info-card",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".info-grid",
            start: "top 85%",
            toggleActions: "play none none none",
          },
        },
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="patients-page-wrapper" ref={containerRef}>
      {/* Header Banner */}
      <header
        className="patients-header"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10, 36, 34, 0.52) 0%, rgba(10, 36, 34, 0.62) 100%), url(${process.env.PUBLIC_URL}/assets/images/patient.png)`,
        }}
      >
        <div className="patients-header-overlay" />
        <div className="patients-header-content">
          <h1>Patients & Visitors</h1>
          <p>
           Everything you need for a smooth hospital visit, including patient information, admission guidance, visitor support and healthcare resources.
          </p>
        </div>
      </header>

      {/* Planning & Guides Grid */}
      <div className="info-grid-container">
        <div className="info-grid">
          {/* Card 1: Planning Your Stay */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="card-icon">
                <FiBriefcase />
              </div>
              <h3>Planning Your Stay</h3>
            </div>
            <p>
              A little preparation can make your time with us more comfortable.
              Here is a quick guide to what to bring.
            </p>
            <ul className="info-list">
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  <strong>Bring:</strong> ID proof, insurance card, current
                  medicines.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  <strong>Pre-admission:</strong> Confirm room type & required
                  paperwork.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  <strong>Comfort:</strong> Slippers, reading material, light
                  jacket.
                </span>
              </li>
            </ul>
          </div>

          {/* Card 2: Patient Guides */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="card-icon">
                <FiFileText />
              </div>
              <h3>Patient Guides</h3>
            </div>
            <p>
              Understand the workflows and steps for a smooth medical
              consultation and hospital experience.
            </p>
            <ul className="info-list">
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  <strong>Inpatient:</strong> Process of admission, daily
                  rounds, meals, and discharge.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  <strong>Outpatient:</strong> Easy registration, doctor
                  consultation, and diagnostic tests.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  <strong>Family:</strong> Patient condition updates and staying
                  in touch.
                </span>
              </li>
            </ul>
          </div>

          {/* Card 3: Billing & Insurance */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="card-icon">
                <FiCreditCard />
              </div>
              <h3>Billing & Insurance</h3>
            </div>
            <p>
              Keeping financial questions transparent, clear, and stress-free.
              Cashless support desk available.
            </p>
            <ul className="info-list">
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  Estimated costs and options provided before admission.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  Guidance on cashless insurance approvals & reimbursement
                  claims.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet" />
                <span>
                  We support and accept major insurance plans, cards & online
                  payments.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Visiting Hours & Policies */}
      <section className="visiting-section">
        <div className="visiting-container">
          <div className="visiting-content">
            <span className="section-tag">Policies</span>
            <h2 className="section-title">Visiting Hours & Policies</h2>
            <p className="section-subtitle">
              Designed to balance rest, recovery, and family support for our
              patients.
            </p>

            <div className="visiting-hours-box">
              <span className="visiting-time">10:00 AM – 7:00 PM</span>
              <span className="visiting-desc">
                Standard visiting hours (All Days)
              </span>
            </div>

            <ul className="info-list" style={{ marginTop: "25px" }}>
              <li>
                <FiCheckCircle className="check-bullet green" />
                <span>
                  Only two visitors per patient allowed at a time inside the
                  ward.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet green" />
                <span>
                  Please avoid visiting if you are suffering from cold, cough,
                  or fever.
                </span>
              </li>
              <li>
                <FiCheckCircle className="check-bullet green" />
                <span>
                  Children under the age of 12 are generally not permitted in
                  patient wards.
                </span>
              </li>
            </ul>
          </div>

          <div className="visiting-graphic">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/img12.png`}
              alt="Visiting Hours"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "20px",
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default PatientsPage;
